import React, { useEffect, useRef, useState } from 'react';
import { useStyletron } from 'baseui';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { IEmailAttachmentsData, IObjProps } from 'shared/consts/types';
import { decodeAndSanitizeInput, getProfileColor, labelWithCharacterLimit } from 'shared/helpers';
import {
	emailComponentCss,
	processUserEmailBody,
	setInlineImages,
	toggleCloseEmailThreadIcon,
	togglePreviousEmail,
} from 'shared/helpers/emailPreviewHelpers';
import Ellipsis from 'shared/icons/Ellipsis';

import { messageOverviewAPI } from 'store/Message/messageOverview/api';
import { RootState } from 'store/rootReducer';

import MessageAvatar from '../../MessageAvatar';

import Attachments from './Attachments';
import EmailHeader from './EmailHeader';
import Recipients from './Recipients';

const UserEmail = ({
	colors,
	isLastMessage,
	index,
	isOpen = false,
	message,
	messageContainer,
	profileColor,
	showEmail,
}: {
	colors: IObjProps;
	isLastMessage: boolean;
	index: number;
	isOpen: boolean;
	message: IObjProps;
	messageContainer: any;
	profileColor: string;
	showEmail: any;
}) => {
	const { emailData, emailBodyProp } = message;
	const { to, htmlBasicMessageToken: token, emailAttachments, cc, userName, from } = emailData;

	const [css, theme]: [any, any] = useStyletron();
	const { t } = useTranslation(['common', 'components']);

	const cid = useSelector((state: RootState) => state.User.botAdmin.data.cid);
	const selectedUser = useSelector((state: RootState) => state.Message.selectedUser);

	const [body, setBody] = useState<any>(null);
	const [showPreviousMail, setShowPreviousMail] = useState<boolean>(false);
	const [processedHtml, setProcessedHtml] = useState<boolean>(false);
	const [initialScrollProcessed, setInitialScrollProcessed] = useState<boolean>(false);
	const [isPreviousMailPresent, setIsPreviousMailPresent] = useState<boolean>(false);

	const bodyRef = useRef<any>(null);
	const scrollRef = useRef<any>(null);

	const toAddress: Array<IObjProps> = Array.isArray(to) ? to : [];
	const attachments: Array<IEmailAttachmentsData> = Array.isArray(emailAttachments)
		? emailAttachments.filter((attachment: IEmailAttachmentsData) => !attachment.inlineContent)
		: [];
	const inlineContent: Array<IEmailAttachmentsData> = Array.isArray(emailAttachments)
		? emailAttachments.filter((attachment: IEmailAttachmentsData) => attachment.inlineContent)
		: [];
	const messageString = emailData.emailSnippet ? emailData.emailSnippet : '-';

	const getEmailContext = () => {
		token &&
			messageOverviewAPI.getEmailContext(cid, token).then((response: any) => {
				if (response.status === 200) {
					setBody(response.data);
					if (response.data && response.data.indexOf('gmail_quote') > -1) {
						setIsPreviousMailPresent(true);
					}
				}
			});
		emailBodyProp && setBody(emailBodyProp);
	};

	useEffect(() => {
		isOpen && !body && getEmailContext();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	useEffect(() => {
		if (showPreviousMail) {
			togglePreviousEmail('block', bodyRef);
		} else {
			togglePreviousEmail('none', bodyRef);
		}
	}, [showPreviousMail, body]);

	useEffect(() => {
		!isOpen && showPreviousMail && setShowPreviousMail(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	useEffect(() => {
		if (body && bodyRef.current && !processedHtml) {
			try {
				processUserEmailBody(bodyRef, colors, setShowPreviousMail);
			} catch {
				setShowPreviousMail(false);
				setProcessedHtml(false);
			}
			setProcessedHtml(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [body]);

	useEffect(() => {
		if (body && scrollRef.current && messageContainer.current && isLastMessage && !initialScrollProcessed) {
			messageContainer.current.scrollTop = scrollRef.current.offsetTop - 40;
			setInitialScrollProcessed(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [body]);

	useEffect(() => {
		if (body && inlineContent.length > 0) {
			const newBody = setInlineImages(body, inlineContent);
			setBody(newBody);
		}
	}, [inlineContent]);

	return (
		<div
			className={css({
				...emailComponentCss,
				backgroundColor: colors.user,
			})}
			ref={scrollRef}
		>
			<div
				className={css({
					display: 'flex',
				})}
			>
				<div>
					<MessageAvatar
						backgroundColor={profileColor || getProfileColor(selectedUser?.id || '0')}
						color={theme.colors.primaryB}
						name={userName || from.personal || from.address}
					/>
				</div>
				<div
					className={css({
						flexGrow: 1,
						paddingLeft: '0.5rem',
					})}
				>
					<EmailHeader
						from={from.address}
						userName={userName || from.personal || from.address}
						timestamp={message.timestamp}
						isOpen={isOpen}
						hasAttachments={attachments.length > 0}
						colors={colors}
					/>
					{isOpen && (
						<div
							className={css({
								fontSize: '0.75rem',
								color: colors.secondaryText,
								lineHeight: '1,25rem',
								display: 'flex',
							})}
						>
							<div
								className={css({
									paddingRight: '0.25rem',
								})}
							>{`${t('components:emailMessagesPreview.to')}:`}</div>
							<div
								className={css({
									flexGrow: 1,
									display: 'flex',
								})}
							>
								<span
									className={css({
										paddingRight: '0.5rem',
									})}
								>
									{toAddress.map((el: IObjProps) => el.address).join(',')}
								</span>
								{cc.length > 0 && (
									<Recipients ccList={cc.map((obj: IObjProps) => obj.address)} colors={colors} />
								)}
							</div>
						</div>
					)}
					<div
						className={css({
							backgroundColor: 'white',
							color: 'black',
							display: isOpen ? 'block' : 'none',
						})}
						ref={bodyRef}
					>
						<span
							dangerouslySetInnerHTML={{
								__html: decodeAndSanitizeInput(body, { FORBID_TAGS: ['style'] }),
							}}
						/>
					</div>
					<div
						className={css({
							display: isOpen ? 'none' : 'block',
							cursor: 'pointer',
							fontSize: '0.875rem',
							lineHeight: '1.25rem',
						})}
						onClick={() => showEmail(index)}
					>
						{messageString.length > 80 ? labelWithCharacterLimit(80, messageString) : messageString}
					</div>
					{isOpen && isPreviousMailPresent && !showPreviousMail && (
						<div
							className={css({
								cursor: 'pointer',
								display: 'inline-block',
							})}
							onClick={() => {
								setShowPreviousMail(true);
								toggleCloseEmailThreadIcon('block', bodyRef);
							}}
						>
							<Ellipsis fill={colors.secondaryText} />
						</div>
					)}
					{isOpen && attachments.length > 0 && (
						<Attachments
							attachments={attachments}
							processName={!emailBodyProp}
							attachmentFocusColor={colors.userAttachmentFocus}
							colors={colors}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default React.memo(UserEmail);
